import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend,
} from "recharts";

const PATH_TO_KEY = {
  "/admin/reports/sales": "daily",
  "/admin/reports/revenue": "monthly",
  "/admin/reports/popular": "popular",
  "/admin/reports/customers": "customers",
  "/admin/reports/downloads": "downloads",
};

const fmtUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const AdminReports = () => {
  const loc = useLocation();
  const key = PATH_TO_KEY[loc.pathname] || "daily";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const urls = {
      daily: "/admin/reports/sales-daily?days=30",
      monthly: "/admin/reports/sales-monthly?months=12",
      popular: "/admin/stats/top-plans?metric=views&limit=15",
      customers: "/admin/reports/top-customers?limit=15",
      downloads: "/admin/stats/top-plans?metric=downloads&limit=15",
    };
    api.get(urls[key])
      .then((r) => setRows(r.data))
      .catch((e) => toast.error("Failed to load report", { description: e.response?.data?.detail || "" }))
      .finally(() => setLoading(false));
  }, [key]);

  const titles = {
    daily: "Daily sales — last 30 days",
    monthly: "Monthly revenue — last 12 months",
    popular: "Most-viewed plans",
    customers: "Top customers by spend",
    downloads: "Most-downloaded plans",
  };

  const renderChart = () => {
    if (rows.length === 0) return <div className="p-12 text-center text-sm text-muted-foreground" data-testid="report-empty">No data for this report yet.</div>;
    const COLOR = "#C85A32";
    if (key === "daily") {
      return (
        <ResponsiveContainer width="100%" height={340}>
          <LineChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d3" />
            <XAxis dataKey="day" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip formatter={(v, k) => k === "revenue" ? fmtUSD(v) : v} />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke={COLOR} strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="orders" stroke="#2C4C3B" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    if (key === "monthly") {
      return (
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d3" />
            <XAxis dataKey="month" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip formatter={(v, k) => k === "revenue" ? fmtUSD(v) : v} />
            <Legend />
            <Bar dataKey="revenue" fill={COLOR} />
            <Bar dataKey="orders" fill="#2C4C3B" />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (key === "popular" || key === "downloads") {
      const dataKey = key === "popular" ? "views" : "downloads";
      return (
        <ResponsiveContainer width="100%" height={40 * rows.length + 40}>
          <BarChart data={rows} layout="vertical" margin={{ left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d3" />
            <XAxis type="number" fontSize={11} />
            <YAxis type="category" dataKey="title" width={160} fontSize={10} />
            <Tooltip />
            <Bar dataKey={dataKey} fill={COLOR} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    // customers
    return (
      <ResponsiveContainer width="100%" height={40 * rows.length + 40}>
        <BarChart data={rows} layout="vertical" margin={{ left: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d3" />
          <XAxis type="number" fontSize={11} />
          <YAxis type="category" dataKey={(r) => r.email || r.name || r.user_id} width={200} fontSize={10} />
          <Tooltip formatter={(v, k) => k === "spend" ? fmtUSD(v) : v} />
          <Legend />
          <Bar dataKey="spend" fill={COLOR} />
          <Bar dataKey="orders" fill="#2C4C3B" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <AdminLayout title={titles[key]}>
      <div className="border border-border bg-card p-6" data-testid="report-container">
        {loading ? <div className="text-muted-foreground">Loading…</div> : renderChart()}
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
